import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  Download,
  Eye,
  MoreHorizontal,
  ArrowDownRight,
  Building2,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { apiService } from "@/services/api";

interface Transaction {
  id: string;
  userId: string;
  userType: "worker" | "employer";
  userName: string;
  amount: number;
  currency: string;
  type: "subscription" | "job_payment" | "refund" | "fee";
  status: "pending" | "completed" | "failed" | "refunded" | "disputed";
  paymentMethod: string;
  gateway: string;
  transactionFee: number;
  createdAt: Date;
  processedAt?: Date;
  description: string;
  metadata?: Record<string, unknown>;
}

interface PaymentMetrics {
  totalRevenue: number;
  totalTransactions: number;
  successRate: number;
  averageTransactionValue: number;
  pendingAmount: number;
  refundedAmount: number;
  disputedAmount: number;
}

interface RefundRequest {
  id: string;
  transactionId: string;
  userId: string;
  userName: string;
  amount: number;
  reason: string;
  status: "pending" | "approved" | "rejected";
  requestedAt: Date;
  processedAt?: Date;
  adminNotes?: string;
}

export function PaymentProcessingDashboard() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [metrics, setMetrics] = useState<PaymentMetrics>({
    totalRevenue: 0,
    totalTransactions: 0,
    successRate: 0,
    averageTransactionValue: 0,
    pendingAmount: 0,
    refundedAmount: 0,
    disputedAmount: 0,
  });
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [selectedRefund, setSelectedRefund] = useState<RefundRequest | null>(null);
  const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);
  const [isRefundDetailOpen, setIsRefundDetailOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundReason, setRefundReason] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Load data from API
  useEffect(() => {
    loadData();
  }, [currentPage, searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadTransactions(), loadRefundRequests(), loadMetrics()]);
    } catch (error) {
      toast.error("Failed to load payment data");
      console.error("Error loading payment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await apiService.getAllTransactions({
        page: currentPage,
        filter: searchTerm,
        status: statusFilter,
      });

      if (response.type === 1) {
        const apiTransactions = response.data.history.map((txn: any) => ({
          id: txn._id,
          userId: txn.userId,
          userType: txn.userType,
          userName: txn.userName,
          amount: txn.amount,
          currency: txn.currency,
          type: txn.type,
          status: txn.status,
          paymentMethod: txn.paymentMethod,
          gateway: txn.gateway,
          transactionFee: txn.transactionFee,
          createdAt: new Date(txn.createdAt),
          processedAt: txn.processedAt ? new Date(txn.processedAt) : undefined,
          description: txn.description,
          metadata: txn.metadata,
        }));
        setTransactions(apiTransactions);
      } else {
        toast.error(response.message || "Failed to load transactions");
      }
    } catch (error) {
      console.error("Error loading transactions:", error);
    }
  };

  const loadRefundRequests = async () => {
    try {
      const response = await apiService.getAllRefundRequests({
        page: currentPage,
      });

      if (response.type === 1) {
        const apiRefunds = response.data.history.map((refund: any) => ({
          id: refund._id,
          transactionId: refund.transactionId,
          userId: refund.userId,
          userName: refund.userName,
          amount: refund.amount,
          reason: refund.reason,
          status: refund.status,
          requestedAt: new Date(refund.requestedAt),
          processedAt: refund.processedAt ? new Date(refund.processedAt) : undefined,
          adminNotes: refund.adminNotes,
        }));
        setRefundRequests(apiRefunds);
      } else {
        toast.error(response.message || "Failed to load refund requests");
      }
    } catch (error) {
      console.error("Error loading refund requests:", error);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await apiService.getPaymentMetrics();

      if (response.type === 1) {
        setMetrics({
          totalRevenue: response.data.totalRevenue || 0,
          totalTransactions: response.data.totalTransactions || 0,
          successRate: response.data.successRate || 0,
          averageTransactionValue: response.data.averageTransactionValue || 0,
          pendingAmount: response.data.pendingAmount || 0,
          refundedAmount: response.data.refundedAmount || 0,
          disputedAmount: response.data.disputedAmount || 0,
        });
      } else {
        toast.error(response.message || "Failed to load payment metrics");
      }
    } catch (error) {
      console.error("Error loading payment metrics:", error);
    }
  };

  // Action handlers
  const handleUpdateTransactionStatus = async (transactionId: string, status: string) => {
    try {
      const response = await apiService.updateTransactionStatus(transactionId, status);
      if (response.type === 1) {
        toast.success("Transaction status updated successfully");
        await loadTransactions();
      } else {
        toast.error(response.message || "Failed to update transaction status");
      }
    } catch (error) {
      toast.error("Failed to update transaction status");
      console.error("Error updating transaction status:", error);
    }
  };

  const handleUpdateRefundStatus = async (refundId: string, status: string) => {
    try {
      const response = await apiService.updateRefundRequestStatus(refundId, status);
      if (response.type === 1) {
        toast.success("Refund request status updated successfully");
        await loadRefundRequests();
      } else {
        toast.error(response.message || "Failed to update refund request status");
      }
    } catch (error) {
      toast.error("Failed to update refund request status");
      console.error("Error updating refund request status:", error);
    }
  };

  const handleCreateRefund = async () => {
    if (!selectedTransaction || !refundAmount || !refundReason) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await apiService.createRefundRequest({
        transactionId: selectedTransaction.id,
        amount: parseFloat(refundAmount),
        reason: refundReason,
      });

      if (response.type === 1) {
        toast.success("Refund request created successfully");
        setIsRefundDialogOpen(false);
        setRefundAmount("");
        setRefundReason("");
        await loadRefundRequests();
      } else {
        toast.error(response.message || "Failed to create refund request");
      }
    } catch (error) {
      toast.error("Failed to create refund request");
      console.error("Error creating refund request:", error);
    }
  };

  // Mock data for fallback (remove this when API is fully working)
  const initializeData = useCallback(() => {
    // Sample transactions
    const sampleTransactions: Transaction[] = [
      {
        id: "txn_001",
        userId: "user_001",
        userType: "employer",
        userName: "Grand Bistro",
        amount: 299.99,
        currency: "USD",
        type: "subscription",
        status: "completed",
        paymentMethod: "Credit Card",
        gateway: "Stripe",
        transactionFee: 8.99,
        createdAt: new Date(Date.now() - 86400000),
        processedAt: new Date(Date.now() - 86400000 + 5000),
        description: "Premium Plan - Monthly Subscription",
        metadata: {
          plan: "premium",
          duration: "monthly",
        },
      },
      {
        id: "txn_002",
        userId: "user_002",
        userType: "employer",
        userName: "Luxury Inn",
        amount: 199.99,
        currency: "USD",
        type: "subscription",
        status: "completed",
        paymentMethod: "Credit Card",
        gateway: "Stripe",
        transactionFee: 5.99,
        createdAt: new Date(Date.now() - 172800000),
        processedAt: new Date(Date.now() - 172800000 + 3000),
        description: "Standard Plan - Monthly Subscription",
        metadata: {
          plan: "standard",
          duration: "monthly",
        },
      },
      {
        id: "txn_003",
        userId: "user_003",
        userType: "worker",
        userName: "John Doe",
        amount: 25.0,
        currency: "USD",
        type: "job_payment",
        status: "completed",
        paymentMethod: "Direct Deposit",
        gateway: "ACH",
        transactionFee: 0.5,
        createdAt: new Date(Date.now() - 3600000),
        processedAt: new Date(Date.now() - 3600000 + 2000),
        description: "Payment for Server Shift - 4 hours",
        metadata: {
          jobId: "job_001",
          hours: 4,
          hourlyRate: 6.25,
        },
      },
      {
        id: "txn_004",
        userId: "user_004",
        userType: "employer",
        userName: "Tech Solutions Inc",
        amount: 499.99,
        currency: "USD",
        type: "subscription",
        status: "pending",
        paymentMethod: "Credit Card",
        gateway: "Stripe",
        transactionFee: 14.99,
        createdAt: new Date(),
        description: "Enterprise Plan - Monthly Subscription",
        metadata: {
          plan: "enterprise",
          duration: "monthly",
        },
      },
      {
        id: "txn_005",
        userId: "user_005",
        userType: "worker",
        userName: "Jane Smith",
        amount: 30.0,
        currency: "USD",
        type: "job_payment",
        status: "failed",
        paymentMethod: "Direct Deposit",
        gateway: "ACH",
        transactionFee: 0.5,
        createdAt: new Date(Date.now() - 7200000),
        description: "Payment for Bartender Shift - 5 hours",
        metadata: {
          jobId: "job_002",
          hours: 5,
          hourlyRate: 6.0,
        },
      },
    ];

    // Sample refund requests
    const sampleRefunds: RefundRequest[] = [
      {
        id: "ref_001",
        transactionId: "txn_006",
        userId: "user_006",
        userName: "Small Cafe",
        amount: 99.99,
        reason: "Service not as expected, multiple complaints from workers",
        status: "pending",
        requestedAt: new Date(Date.now() - 86400000),
      },
      {
        id: "ref_002",
        transactionId: "txn_007",
        userId: "user_007",
        userName: "Local Restaurant",
        amount: 149.99,
        reason: "Downgrading to smaller plan, overcharged",
        status: "approved",
        requestedAt: new Date(Date.now() - 172800000),
        processedAt: new Date(Date.now() - 86400000),
        adminNotes: "Customer downgraded plan, partial refund approved",
      },
    ];

    setTransactions(sampleTransactions);
    setRefundRequests(sampleRefunds);
    calculateMetrics(sampleTransactions);
  }, []);

  const calculateMetrics = (txns: Transaction[]) => {
    const totalRevenue = txns
      .filter(t => t.status === "completed")
      .reduce((sum, t) => sum + t.amount, 0);

    const totalTransactions = txns.length;
    const completedTransactions = txns.filter(t => t.status === "completed").length;
    const successRate =
      totalTransactions > 0 ? (completedTransactions / totalTransactions) * 100 : 0;
    const averageTransactionValue =
      completedTransactions > 0 ? totalRevenue / completedTransactions : 0;

    const pendingAmount = txns
      .filter(t => t.status === "pending")
      .reduce((sum, t) => sum + t.amount, 0);

    const refundedAmount = txns
      .filter(t => t.status === "refunded")
      .reduce((sum, t) => sum + t.amount, 0);

    const disputedAmount = txns
      .filter(t => t.status === "disputed")
      .reduce((sum, t) => sum + t.amount, 0);

    setMetrics({
      totalRevenue,
      totalTransactions,
      successRate,
      averageTransactionValue,
      pendingAmount,
      refundedAmount,
      disputedAmount,
    });
  };

  const handleRefundRequest = async (transactionId: string, amount: number, reason: string) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newRefund: RefundRequest = {
        id: `ref_${Date.now()}`,
        transactionId,
        userId: selectedTransaction?.userId ?? "",
        userName: selectedTransaction?.userName ?? "",
        amount,
        reason,
        status: "pending",
        requestedAt: new Date(),
      };

      setRefundRequests(prev => [newRefund, ...prev]);
      setIsRefundDialogOpen(false);
      setRefundAmount("");
      setRefundReason("");
      toast.success("Refund request created successfully");
    } catch (error: unknown) {
      toast.error("Failed to create refund request");
      console.error("Failed to create refund request:", error);
    }
  };

  const handleRefundApproval = async (refundId: string, approved: boolean, notes?: string) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setRefundRequests(prev =>
        prev.map(refund =>
          refund.id === refundId
            ? {
                ...refund,
                status: approved ? "approved" : "rejected",
                processedAt: new Date(),
                adminNotes: notes,
              }
            : refund
        )
      );

      toast.success(`Refund ${approved ? "approved" : "rejected"} successfully`);
    } catch (error: unknown) {
      console.error("Failed to process refund:", error);
      toast.error("Failed to process refund");
    }
  };

  const getStatusBadge = (status: Transaction["status"]) => {
    const statusConfig: Record<
      Transaction["status"],
      {
        variant: "default" | "secondary" | "destructive" | "outline";
        icon: React.ComponentType<{ className?: string }>;
        text: string;
      }
    > = {
      completed: {
        variant: "default" as const,
        icon: CheckCircle,
        text: "Completed",
      },
      pending: {
        variant: "secondary" as const,
        icon: Clock,
        text: "Pending",
      },
      failed: {
        variant: "destructive" as const,
        icon: XCircle,
        text: "Failed",
      },
      refunded: {
        variant: "outline" as const,
        icon: ArrowDownRight,
        text: "Refunded",
      },
      disputed: {
        variant: "destructive" as const,
        icon: AlertTriangle,
        text: "Disputed",
      },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center space-x-1">
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const getRefundStatusBadge = (status: RefundRequest["status"]) => {
    const statusConfig: Record<
      RefundRequest["status"],
      {
        variant: "default" | "secondary" | "destructive";
        icon: React.ComponentType<{ className?: string }>;
        text: string;
      }
    > = {
      pending: {
        variant: "secondary" as const,
        icon: Clock,
        text: "Pending",
      },
      approved: {
        variant: "default" as const,
        icon: CheckCircle,
        text: "Approved",
      },
      rejected: {
        variant: "destructive" as const,
        icon: XCircle,
        text: "Rejected",
      },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center space-x-1">
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (searchTerm && !transaction.userName.toLowerCase().includes(searchTerm.toLowerCase()))
      return false;
    if (statusFilter && transaction.status !== statusFilter) return false;
    return true;
  });

  const filteredRefunds = refundRequests.filter(refund => {
    if (searchTerm && !refund.userName.toLowerCase().includes(searchTerm.toLowerCase()))
      return false;
    if (statusFilter && refund.status !== statusFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading payment data...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Payment Processing Dashboard</h2>
          <p className="text-muted-foreground">
            Monitor transactions, manage refunds, and track payment analytics
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              window.print();
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              initializeData();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Payment Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalTransactions} transactions
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalTransactions -
                Math.floor((metrics.totalTransactions * metrics.successRate) / 100)}{" "}
              failed
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Pending Amount</CardTitle>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.pendingAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Awaiting processing</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Refunded Amount</CardTitle>
            <ArrowDownRight className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.refundedAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Total refunds issued</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="refunds">Refund Requests</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment Transactions</CardTitle>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute w-4 h-4 left-3 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Search transactions..."
                    value={searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value);
                    }}
                    className="w-64 pl-10"
                  />
                </div>
                <select
                  className="p-2 border rounded-md"
                  value={statusFilter}
                  onChange={e => {
                    setStatusFilter(e.target.value);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                  <option value="disputed">Disputed</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Gateway</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map(transaction => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-mono text-sm">{transaction.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {transaction.userType === "employer" ? (
                            <Building2 className="w-4 h-4 text-blue-600" />
                          ) : (
                            <User className="w-4 h-4 text-green-600" />
                          )}
                          <span>{transaction.userName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        ${transaction.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{transaction.type.replace("_", " ")}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                      <TableCell>{transaction.gateway}</TableCell>
                      <TableCell>{format(transaction.createdAt, "MMM dd, yyyy")}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setIsTransactionDetailOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {transaction.status === "pending" && (
                              <DropdownMenuItem
                                onClick={() => {
                                  void handleUpdateTransactionStatus(transaction.id, "completed");
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark as Completed
                              </DropdownMenuItem>
                            )}
                            {transaction.status === "completed" && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                  setIsRefundDialogOpen(true);
                                }}
                              >
                                <ArrowDownRight className="w-4 h-4 mr-2" />
                                Process Refund
                              </DropdownMenuItem>
                            )}
                            {transaction.status === "pending" && (
                              <DropdownMenuItem
                                onClick={() => {
                                  void handleUpdateTransactionStatus(transaction.id, "failed");
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Mark as Failed
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Refunds Tab */}
        <TabsContent value="refunds" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Refund Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Refund ID</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRefunds.map(refund => (
                    <TableRow key={refund.id}>
                      <TableCell className="font-mono text-sm">{refund.id}</TableCell>
                      <TableCell>{refund.userName}</TableCell>
                      <TableCell className="font-medium">${refund.amount.toFixed(2)}</TableCell>
                      <TableCell className="max-w-xs truncate">{refund.reason}</TableCell>
                      <TableCell>{getRefundStatusBadge(refund.status)}</TableCell>
                      <TableCell>{format(refund.requestedAt, "MMM dd, yyyy")}</TableCell>
                      <TableCell>
                        {refund.status === "pending" ? (
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                void handleUpdateRefundStatus(refund.id, "approved");
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                void handleUpdateRefundStatus(refund.id, "rejected");
                              }}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedRefund(refund);
                                  setIsRefundDetailOpen(true);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Transaction Volume by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {["subscription", "job_payment", "refund", "fee"].map(type => {
                    const typeTransactions = transactions.filter(t => t.type === type);
                    const total = typeTransactions.reduce((sum, t) => sum + t.amount, 0);
                    const count = typeTransactions.length;

                    return (
                      <div key={type} className="flex items-center justify-between">
                        <span className="capitalize">{type.replace("_", " ")}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-muted-foreground">
                            {count} transactions
                          </span>
                          <span className="font-medium">${total.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Success Rate by Gateway</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {["Stripe", "ACH", "PayPal"].map(gateway => {
                    const gatewayTransactions = transactions.filter(t => t.gateway === gateway);
                    const total = gatewayTransactions.length;
                    const successful = gatewayTransactions.filter(
                      t => t.status === "completed"
                    ).length;
                    const rate = total > 0 ? (successful / total) * 100 : 0;

                    return (
                      <div key={gateway} className="flex items-center justify-between">
                        <span>{gateway}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-muted-foreground">
                            {successful}/{total}
                          </span>
                          <span className="font-medium">{rate.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Dialog */}
      <Dialog open={isTransactionDetailOpen} onOpenChange={setIsTransactionDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Transaction ID</Label>
                  <p className="font-mono text-sm">{selectedTransaction.id}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedTransaction.status)}</div>
                </div>
                <div>
                  <Label>User</Label>
                  <p>{selectedTransaction.userName}</p>
                </div>
                <div>
                  <Label>Amount</Label>
                  <p className="font-medium">${selectedTransaction.amount.toFixed(2)}</p>
                </div>
                <div>
                  <Label>Transaction Fee</Label>
                  <p>${selectedTransaction.transactionFee.toFixed(2)}</p>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <p>{selectedTransaction.paymentMethod}</p>
                </div>
                <div>
                  <Label>Gateway</Label>
                  <p>{selectedTransaction.gateway}</p>
                </div>
                <div>
                  <Label>Created At</Label>
                  <p>{format(selectedTransaction.createdAt, "PPP 'at' p")}</p>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <p>{selectedTransaction.description}</p>
              </div>
              {selectedTransaction.metadata && (
                <div>
                  <Label>Metadata</Label>
                  <pre className="p-2 text-sm rounded bg-muted">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Request Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>Create a refund request for this transaction</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Transaction Amount</Label>
              <p className="font-medium">${selectedTransaction?.amount.toFixed(2)}</p>
            </div>
            <div>
              <Label htmlFor="refund-amount">Refund Amount</Label>
              <Input
                id="refund-amount"
                type="number"
                value={refundAmount}
                onChange={e => {
                  setRefundAmount(e.target.value);
                }}
                step="0.01"
                min="0"
                max={selectedTransaction?.amount}
                placeholder={selectedTransaction?.amount.toString()}
              />
            </div>
            <div>
              <Label htmlFor="refund-reason">Reason for Refund</Label>
              <Input
                id="refund-reason"
                value={refundReason}
                onChange={e => {
                  setRefundReason(e.target.value);
                }}
                placeholder="Enter reason for refund..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRefundDialogOpen(false);
                setRefundAmount("");
                setRefundReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleCreateRefund();
              }}
            >
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Detail Dialog */}
      <Dialog open={isRefundDetailOpen} onOpenChange={setIsRefundDetailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund Details</DialogTitle>
          </DialogHeader>
          {selectedRefund && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Refund ID</Label>
                  <p className="font-mono text-sm">{selectedRefund.id}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getRefundStatusBadge(selectedRefund.status)}</div>
                </div>
                <div>
                  <Label>User</Label>
                  <p>{selectedRefund.userName}</p>
                </div>
                <div>
                  <Label>Amount</Label>
                  <p className="font-medium">${selectedRefund.amount.toFixed(2)}</p>
                </div>
                <div>
                  <Label>Requested At</Label>
                  <p>{format(selectedRefund.requestedAt, "PPP 'at' p")}</p>
                </div>
                {selectedRefund.processedAt && (
                  <div>
                    <Label>Processed At</Label>
                    <p>{format(selectedRefund.processedAt, "PPP 'at' p")}</p>
                  </div>
                )}
              </div>
              <div>
                <Label>Reason</Label>
                <p>{selectedRefund.reason}</p>
              </div>
              {selectedRefund.adminNotes && (
                <div>
                  <Label>Admin Notes</Label>
                  <p>{selectedRefund.adminNotes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
